import { Form, Formik } from 'formik';
import PropTypes from 'prop-types';
import { useEffect, useMemo, useState } from 'react';
import { FaPencilAlt } from 'react-icons/fa';
import { Col, Label, Row } from 'reactstrap';
import {
  getGLTemplateId,
  getGLTemplateName,
  getGLTemplates,
  getGLTemplatesCount,
} from '../../../api/glTemplate';
import { Button, Pagination, Select, Table } from '../../../common/components';
import { useSetLoading } from '../../../common/hooks/appearance';
import { usePagination } from '../../../common/hooks/data';
import { apiErrorHandler } from '../../../utils/handlers';
import AutoCompleteDropdown from '../common/AutoCompleteDropdown';
import { ACTION_PAGINATION, ACTION_SEARCH, GLTTableColumns, gltSearchSchema } from './constants';
import GLTListEmptyTile from './GLTListEmptyTile';

const GLTemplateListingTable = (props) => {
  const { handleTemplateAction, glTypesData, glCategoriesData } = props;
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const { page, limit, setPage, setLimit } = usePagination();
  const [searchValues, setSearchValues] = useState(null);
  const [applyFilters, setApplyFilters] = useState(false);
  const [noSearchResults, setNoSearchResults] = useState(false);

  const fetchGLTemplateData = async (values, action = ACTION_SEARCH, pagee = 1, limitt = 5) => {
    if (action === ACTION_SEARCH) {
      setSearchValues(values);
      setPage(pagee);
      setLimit(limitt);
    }
    setLoading(true);
    try {
      const params = {};
      if (values) {
        params.templateName = values.gltName?.value || '';
        params.templateTypeId = values.gltType?.value !== 'all' ? values.gltType?.value : '';

        params.templateCategoryId =
          values.gltCategory?.value !== 'all' ? values.gltCategory?.value : '';
        params.identificationNumber = values.gltId?.value || '';
      }

      const listParams = {
        page: pagee,
        limit: limitt,
        ...params,
      };

      const templatesResp = await getGLTemplates(listParams);
      setData(templatesResp.data.data);

      // call this only when search value is changed and not with pagination
      if (action === ACTION_SEARCH) {
        const countResp = await getGLTemplatesCount(params);
        setTotalCount(countResp.data.data.totalCount);
        if (countResp.data.data.totalCount === 0) {
          setNoSearchResults(true);
        } else {
          setNoSearchResults(false);
        }
      }

      setLoading(false);
      setApplyFilters(false);
    } catch (err) {
      setLoading(false);
      apiErrorHandler(err);
    }
  };

  const selectAllOption = { label: 'Select All', value: 'all' };
  const glTypesOptions = [selectAllOption];
  const glCategoriesOptions = [selectAllOption];

  if (glTypesData?.length > 0) {
    glTypesOptions.push(...glTypesData);
  }
  if (glCategoriesData?.length > 0) {
    glCategoriesOptions.push(...glCategoriesData);
  }

  useEffect(() => {
    if (applyFilters) fetchGLTemplateData(searchValues, ACTION_PAGINATION, page, limit);
  }, [page, limit, applyFilters, searchValues]);

  const showLoading = useMemo(() => loading, [loading]);
  useSetLoading(showLoading);

  return (
    <>
      <Formik
        initialValues={{
          gltId: null,
          gltName: null,
          gltType: null,
          gltCategory: null,
        }}
        onSubmit={(values) => fetchGLTemplateData(values, ACTION_SEARCH)}
        enableReinitialize
        validationSchema={gltSearchSchema}
      >
        {({ handleSubmit, values, setFieldValue }) => {
          return (
            <Form onSubmit={handleSubmit}>
              <Row>
                <Col lg="6" className="account-number-input">
                  <Label>GL Identification number</Label>
                  <AutoCompleteDropdown
                    name="gltId"
                    selection={values.gltId}
                    setSelection={(value) => setFieldValue('gltId', value)}
                    apiMethod={getGLTemplateId}
                    responseFormatter={(d) => ({
                      value: d.identificationNumber,
                      label: d.identificationNumber,
                    })}
                    dropdownPlaceholder="GL - 1200"
                    loadDefault
                    loadOnClear
                  />
                </Col>
                <Col lg="6" className="account-number-input">
                  <Label>GL Template name</Label>
                  <AutoCompleteDropdown
                    name="gltName"
                    selection={values.gltName}
                    setSelection={(value) => setFieldValue('gltName', value)}
                    apiMethod={getGLTemplateName}
                    responseFormatter={(d) => ({
                      value: d.templateName,
                      label: d.templateName,
                    })}
                    dropdownPlaceholder="GL Template Name"
                    loadDefault
                    loadOnClear
                  />
                </Col>
              </Row>
              <Row>
                <Col lg="6" className="atd-input account-number-input gl-tp-input">
                  <Label>GL Template type</Label>
                  <div className="common-local-search w-100">
                    <Select
                      classNamePrefix="select-dropdown"
                      size="lg"
                      name="gltType"
                      formInput
                      options={glTypesOptions}
                      onChange={(value) => setFieldValue('gltType', value)}
                      isClearable
                      value={values.gltType}
                    />
                  </div>
                </Col>
                <Col lg="6" className="atd-input account-number-input gl-tp-input">
                  <Label>GL template category</Label>
                  <div className="common-local-search w-100">
                    <Select
                      classNamePrefix="select-dropdown"
                      size="lg"
                      name="gltCategory"
                      formInput
                      options={glCategoriesOptions}
                      onChange={(value) => setFieldValue('gltCategory', value)}
                      isClearable
                      value={values.gltCategory}
                    />
                  </div>
                </Col>
              </Row>
              <Row>
                <Col>
                  <div className="segment-btns">
                    <Button color="primary" className="atd-btn" size="xs1" type="submit">
                      Search
                    </Button>
                    <Button
                      color="primary"
                      className="atd-btn"
                      size="xs1"
                      onClick={() => handleTemplateAction()}
                    >
                      Create New
                    </Button>
                  </div>
                </Col>
              </Row>
            </Form>
          );
        }}
      </Formik>
      {data && data?.length > 0 && (
        <>
          <h4 className="atd-block-heading">GL Template Details</h4>
          <div className="table-resp-scroll">
            <Table
              headers={GLTTableColumns}
              bordered
              responsive
              className="dashboard-table gl-table"
            >
              <tbody className="gl-table-body gl-table-search">
                {data?.map((rowData) => (
                  <tr key={rowData.templateId}>
                    <td className="ta-center">{rowData.identificationNumber}</td>
                    <td className="ta-center">{rowData.templateName}</td>
                    <td className="ta-center">{rowData.description}</td>
                    <td className="cursor-pointer ta-center">
                      <FaPencilAlt
                        color="#2173AE"
                        onClick={() => handleTemplateAction(rowData.templateId)}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>

          <div className="pagination-araging-report">
            <Pagination
              totalResults={totalCount}
              page={page}
              limit={limit}
              setPage={(val) => {
                setApplyFilters(true);
                setPage(val);
              }}
              setLimit={(val) => {
                setApplyFilters(true);
                setLimit(val);
              }}
            />
          </div>
        </>
      )}
      {data && data?.length === 0 && noSearchResults && (
        <>
          <h4 className="atd-block-heading">GL Template Details</h4>
          <GLTListEmptyTile />
        </>
      )}
    </>
  );
};

GLTemplateListingTable.propTypes = {
  handleTemplateAction: PropTypes.func.isRequired,
  glTypesData: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    }),
  ).isRequired,
  glCategoriesData: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    }),
  ).isRequired,
};

export default GLTemplateListingTable;
